import type { ReactNode } from "react";
import { useId } from "react";
import { ModalPrimaryButton } from "./Button";
import { ASSET_MODAL_CARD, ModalCloseButton, ModalShell } from "./ModalShell";
import { PillToggle } from "./PillToggle";

export interface AssetModalTab<T extends string> {
  id: T;
  label: string;
}

interface AssetModalChromeProps<T extends string> {
  /** "Dismiss {ariaContext}" / "Close {ariaContext}". */
  ariaContext: string;
  /** ReactNode rather than string: an injected name has to stay its own text
   *  node so it can be styled — and queried — independently of the sentence
   *  around it. Build one with <TitleWithSubject>. */
  title: ReactNode;
  subtitle: ReactNode;
  tabs: readonly AssetModalTab<T>[];
  activeTab: T;
  onTabChange: (tab: T) => void;
  /** Footer left slot, usually <SaveAsAssetCheckbox>. Omitted on tabs that
   *  always save, or that can't. */
  footerLeft?: ReactNode;
  primary: { label: string; disabled: boolean; onClick: () => void };
  onClose: () => void;
  /** Tab body. The chrome supplies the horizontal padding. */
  children: ReactNode;
}

/** A modal title with one word carried in from context, e.g.
 *  "Add to <Frame Color> Parameter". The subject is its own element so it can
 *  take the accent colour without the rest of the sentence following it. */
export function TitleWithSubject({
  prefix,
  subject,
  suffix,
}: {
  prefix: string;
  subject: string;
  suffix?: string;
}) {
  return (
    <>
      {prefix} <span className="text-[var(--text-accent)]">{subject}</span>
      {suffix ? <> {suffix}</> : null}
    </>
  );
}

export function AssetModalChrome<T extends string>({
  ariaContext,
  title,
  subtitle,
  tabs,
  activeTab,
  onTabChange,
  footerLeft,
  primary,
  onClose,
  children,
}: AssetModalChromeProps<T>) {
  const titleId = useId();

  return (
    <ModalShell
      onClose={onClose}
      ariaContext={ariaContext}
      labelledBy={titleId}
      cardClassName={ASSET_MODAL_CARD}
      closeAffordance="none"
    >
      <div className="flex flex-col gap-4 px-4 py-6">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-4">
            <h2
              id={titleId}
              className="flex-1 text-[18px] font-bold leading-5 text-[var(--text-primary)]"
            >
              {title}
            </h2>
            <ModalCloseButton
              onClose={onClose}
              ariaContext={ariaContext}
              size={16}
            />
          </div>
          <p className="text-[15px] leading-[1.6] text-[var(--text-muted)]">
            {subtitle}
          </p>
        </div>
        <PillToggle options={tabs} active={activeTab} onChange={onTabChange} />
      </div>

      <div className="px-4">{children}</div>

      <div className="flex items-center justify-end px-4 py-6">
        {footerLeft ? <div className="mr-auto">{footerLeft}</div> : null}
        <ModalPrimaryButton
          onClick={primary.onClick}
          disabled={primary.disabled}
        >
          {primary.label}
        </ModalPrimaryButton>
      </div>
    </ModalShell>
  );
}
